import { t as createLogger } from "./app.shared-CmP4XLF_.js";

import { t as formatMoney } from "./app.shared-DrMetWk9.js";

import { i as isBisEnabled, n as getVariantStatus, t as applyProductFormButtonState } from "./app.shared-qDp0vS1A.js";

var product_form_sticky_default = (() => {
    const log = createLogger("product-form-sticky");
    let lastNudge = null;
    const dispatchNudge = detail => {
        lastNudge = detail;
        log("ui", "dispatch global-ui:nudge", detail);
        document.dispatchEvent(new CustomEvent("global-ui:nudge", {
            detail: detail
        }));
    };
    document.addEventListener("global-ui:nudge-request", event => {
        const {lane: lane} = event.detail ?? {};
        if (!lastNudge) return;
        if (lane !== lastNudge.lane && lane !== "all") return;
        document.dispatchEvent(new CustomEvent("global-ui:nudge", {
            detail: lastNudge
        }));
    });
    if (customElements.get("product-form-sticky")) return;
    customElements.define("product-form-sticky", class ProductFormSticky extends HTMLElement {
        connectedCallback() {
            this.formId = this.dataset.form;
            this.productId = +this.dataset.productId;
            this.sizePosition = +this.dataset.sizeOptionPosition || 0;
            this.productForm = document.querySelector(`product-form[form="${this.formId}"]`);
            this.mainAtc = this.productForm?.querySelector("[data-atc]");
            this.variantSelector = document.querySelector(`variant-selector[data-product-id="${this.productId}"]`);
            this.bar = this.querySelector("[data-sticky]");
            this.mobilePrice = this.querySelector("[data-mobile-price]");
            this.desktopPrice = this.querySelector("[data-desktop-price]");
            this.customSelect = this.querySelector("custom-select");
            this.buttons = this.querySelectorAll("button[data-atc]");
            this.wishlistEls = this.querySelectorAll("wishlist-button[data-variant-id]");
            this.mobileMq = window.matchMedia("(max-width: 1024px)");
            this.ctaVisible = true;
            this.footerVisible = false;
            document.addEventListener("variant:change", this.onVariantChange);
            this.customSelect?.addEventListener("select:change", this.onSelectChange);
            this.buttons.forEach(button => button.addEventListener("click", this.onAtcClick));
            this.setupVisibility();
            this.setupFooterVisibility();
            this.setupPriceObserver();
            this.updateVisibility();
            this.bar?.classList.remove("desktop:hidden");
            this.initialSync();
        }
        disconnectedCallback() {
            document.removeEventListener("variant:change", this.onVariantChange);
            this.customSelect?.removeEventListener("select:change", this.onSelectChange);
            this.buttons.forEach(button => button.removeEventListener("click", this.onAtcClick));
            this.ctaObserver?.disconnect();
            this.footerObserver?.disconnect();
            this.priceObserver?.disconnect();
            this.priceMq?.removeEventListener("change", this.onBreakpointChange);
            this.mobileMq?.removeEventListener("change", this.onCtaBreakpointChange);
        }
        initialSync() {
            const vs = this.variantSelector;
            if (!vs || !vs.currentVariant) return;
            this.onVariantChange({
                detail: {
                    variant: vs.currentVariant,
                    productId: this.productId,
                    options: vs.getSelectedOptions?.()
                }
            });
        }
        onVariantChange=event => {
            if (event.detail.modal) return;
            if (event.detail.productId !== this.productId) return;
            const {variant: variant, options: options} = event.detail;
            this.currentVariant = variant;
            this.updatePrice(variant);
            this.updateSize(variant, options);
            this.syncSizeAvailability();
            this.updateButtonState(variant);
            this.syncWishlist(variant);
        };
        canNotify=() => isBisEnabled() && this.dataset.bisDisabled !== "true";
        getVariantStatus(variant) {
            return getVariantStatus(variant, this.canNotify(), this.dataset.bisHandle || "");
        }
        updatePrice(variant) {
            const html = variant ? formatMoney(variant.price) : "";
            if (this.mobilePrice) this.mobilePrice.innerHTML = html;
            if (this.desktopPrice) this.desktopPrice.innerHTML = html;
        }
        updateSize(variant, options) {
            if (!this.customSelect) return;
            const idx = this.sizePosition - 1;
            const value = options?.[idx] ?? variant?.options?.[idx] ?? "";
            this.customSelect.setSelected(value);
        }
        syncSizeAvailability() {
            if (!this.customSelect || !this.productForm) return;
            const oosValues = Array.from(this.productForm.querySelectorAll(`input[name="option${this.sizePosition}"][aria-out-of-stock]`)).map(radio => radio.value);
            this.customSelect.setAvailability(oosValues);
        }
        updateButtonState(variant) {
            if (this.dataset.appExclusive === "true") return;
            const status = this.getVariantStatus(variant);
            applyProductFormButtonState(this.buttons, status, {
                restock: this.dataset.bisOverrideLabel || this.dataset.bisRestockLabel,
                launch: this.dataset.bisOverrideLabel || this.dataset.bisLaunchLabel,
                notifyMe: this.dataset.bisOverrideLabel
            });
        }
        syncWishlist(variant) {
            this.wishlistEls.forEach(el => {
                el.dataset.variantId = variant?.id ?? "";
                const cents = variant?.price;
                if (Number.isFinite(cents) && cents > 0) el.dataset.productPrice = String(cents / 100);
                el.syncMembership?.();
            });
        }
        onSelectChange=event => {
            const {value: value} = event.detail;
            const radio = this.productForm?.querySelector(`input[name="option${this.sizePosition}"][value="${CSS.escape(value)}"]`);
            if (radio) {
                radio.checked = true;
                radio.dispatchEvent(new Event("change", {
                    bubbles: true
                }));
            }
        };
        onAtcClick=event => {
            if (!this.mobileMq?.matches) return;
            if (!this.currentVariant?.available) return;
            if (!this.variantSelector) return;
            if (!this.productForm?.querySelector("[data-option-radio]")) return;
            event.preventDefault();
            this.scrollToVariantSelector();
        };
        scrollToVariantSelector() {
            const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 71;
            const top = Math.max(0, this.variantSelector.getBoundingClientRect().top + window.scrollY - headerHeight - 12);
            const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
            window.scrollTo({
                top: top,
                behavior: behavior
            });
        }
        setupVisibility() {
            if (!this.mainAtc || !this.bar) return;
            this.observeCta();
            this.mobileMq.addEventListener("change", this.onCtaBreakpointChange);
        }
        observeCta() {
            this.ctaObserver?.disconnect();
            this.ctaObserver = new IntersectionObserver(([entry]) => {
                this.ctaVisible = entry.isIntersecting;
                this.updateVisibility();
            }, {
                rootMargin: this.mobileMq.matches ? "0px" : "0px 0px -10% 0px"
            });
            this.ctaObserver.observe(this.mainAtc);
        }
        onCtaBreakpointChange=() => this.observeCta();
        setupFooterVisibility() {
            if (!this.bar) return;
            this.footer = document.querySelector("footer.section--footer");
            if (!this.footer) return;
            this.footerObserver = new IntersectionObserver(([entry]) => {
                this.footerVisible = entry.isIntersecting;
                this.updateVisibility();
            }, {
                rootMargin: "0px 0px 10% 0px"
            });
            this.footerObserver.observe(this.footer);
        }
        updateVisibility() {
            if (!this.bar) return;
            const visible = !(this.ctaVisible || this.footerVisible);
            this.bar.toggleAttribute("inert", !visible);
            document.documentElement.style.setProperty("--sticky-form-height", visible ? `${this.bar.offsetHeight}px` : "0px");
            const belowCta = this.mainAtc ? this.mainAtc.getBoundingClientRect().bottom < 0 : false;
            this.bar.toggleAttribute("data-revealed", belowCta);
            dispatchNudge({
                lane: "bottom",
                source: "sticky-form",
                isSibling: true,
                transform: visible ? `translateY(-${this.bar.offsetHeight + 12}px)` : "",
                edgeY: visible ? window.innerHeight - this.bar.offsetHeight : null
            });
        }
        setupPriceObserver() {
            if (!this.mobilePrice) return;
            const priceEl = document.querySelector(`variant-price[data-product-id="${this.productId}"]`);
            if (!priceEl) return;
            this.priceMq = window.matchMedia("(max-width: 1024px)");
            if (this.priceMq.matches) {
                const rect = priceEl.getBoundingClientRect();
                const inView = rect.top < window.innerHeight && rect.bottom > 0;
                this.swapPriceBookmark(inView);
            }
            this.onBreakpointChange = () => {
                if (!this.priceMq.matches) this.swapPriceBookmark(true);
            };
            this.priceMq.addEventListener("change", this.onBreakpointChange);
            this.priceObserver = new IntersectionObserver(([entry]) => {
                if (!this.priceMq.matches) return;
                this.swapPriceBookmark(entry.isIntersecting);
            }, {
                threshold: 0
            });
            this.priceObserver.observe(priceEl);
        }
        swapPriceBookmark(priceInView) {
            const showPrice = !priceInView;
            this.mobilePrice.classList.toggle("opacity-0", !showPrice);
            this.mobilePrice.classList.toggle("blur-sm", !showPrice);
            this.mobilePrice.setAttribute("aria-hidden", String(!showPrice));
            this.wishlistEls.forEach(el => {
                el.classList.toggle("opacity-0", showPrice);
                el.toggleAttribute("inert", showPrice);
                el.setAttribute("aria-hidden", String(showPrice));
            });
        }
    });
})();